/**
 * A flat number and a phone number — the web app's mirror of `set_contact_details()` in SQL
 * (20261001000300).
 *
 * The database cleans, refuses and stores; this exists so a form can say what is wrong while
 * somebody is still typing, rather than after a round trip. Same rules, same words, held to
 * the same cases in contact.test.ts and supabase/tests/contact_details_test.sql. If the two
 * disagreed, a form would accept something the database then turned away.
 */

export const FLAT_NO_MAX = 20;

export const FLAT_NO_TOO_LONG = "flat number is too long — 20 characters at most";
export const PHONE_INVALID = "phone number needs 10 to 15 digits, optionally starting with +";

const PHONE = /^\+?[0-9]{10,15}$/;

/** Trimmed, or null when there is nothing left. Spaces only, as Postgres's `btrim` does. */
export function cleanFlatNo(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").replace(/^ +| +$/g, "");
  return trimmed === "" ? null : trimmed;
}

/** Without the spaces and hyphens people type, or null when there is nothing left. */
export function cleanPhone(value: string | null | undefined): string | null {
  const stripped = (value ?? "").replace(/[\s-]/g, "");
  return stripped === "" ? null : stripped;
}

export interface ContactProblem {
  field: "flatNo" | "phone";
  message: string;
}

/**
 * The first thing wrong, in the order the database checks: flat number, then phone. Null when
 * both would be accepted — blank included, since both are optional.
 */
export function contactProblem(
  flatNo: string | null | undefined,
  phone: string | null | undefined,
): ContactProblem | null {
  const flat = cleanFlatNo(flatNo);
  // Code points, not UTF-16 units: `char_length` counts characters.
  if (flat !== null && [...flat].length > FLAT_NO_MAX) {
    return { field: "flatNo", message: FLAT_NO_TOO_LONG };
  }

  const cleaned = cleanPhone(phone);
  if (cleaned !== null && !PHONE.test(cleaned)) {
    return { field: "phone", message: PHONE_INVALID };
  }

  return null;
}
